import React from 'react';
import { Box, Typography } from '@mui/material';
import { AccessTime as AccessTimeIcon } from '@mui/icons-material';

/**
 * Formats store hours into a readable string.
 * @param {Array<{day: string, startTime: string, endTime: string}>} hours
 * @returns {string}
 */
const formatHours = (hours) => {
    if (!hours || hours.length === 0) {
        return 'Horario no disponible';
    }

    const dayNames = {
        monday: 'Lun',
        tuesday: 'Mar',
        wednesday: 'Mié',
        thursday: 'Jue',
        friday: 'Vie',
        saturday: 'Sáb',
        sunday: 'Dom'
    };

    // Group hours by day (hours are already in local timezone)
    const hoursByDay = {};
    hours.forEach(hour => {
        if (!hoursByDay[hour.day]) {
            hoursByDay[hour.day] = [];
        }
        hoursByDay[hour.day].push(`${hour.startTime} - ${hour.endTime}`);
    });

    // Format as string
    const formatted = Object.keys(hoursByDay)
        .sort()
        .map(day => {
            const dayName = dayNames[day] || day;
            const times = hoursByDay[day].join(', ');
            return `${dayName}: ${times}`;
        })
        .join(' | ');

    return formatted;
};

const StoreHours = ({ store }) => {
    if (!store?.hours || store.hours.length === 0) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ 
                        fontSize: '0.7rem',
                        lineHeight: 1.2
                    }}
                >
                    Horario no disponible
                </Typography>
            </Box>
        );
    }

    const dayNames = {
        monday: 'Lunes',
        tuesday: 'Martes',
        wednesday: 'Miércoles',
        thursday: 'Jueves',
        friday: 'Viernes',
        saturday: 'Sábado',
        sunday: 'Domingo'
    };

    // Group hours by day
    const hoursByDay = {};
    store.hours.forEach(hour => {
        if (!hoursByDay[hour.day]) {
            hoursByDay[hour.day] = [];
        }
        hoursByDay[hour.day].push(`${hour.startTime} - ${hour.endTime}`);
    });

    // Sort days in order
    const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const sortedDays = Object.keys(hoursByDay).sort((a, b) => {
        return dayOrder.indexOf(a) - dayOrder.indexOf(b);
    });

    return (
        <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <AccessTimeIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                <Typography 
                    variant="subtitle2" 
                    sx={{ 
                        fontWeight: 600,
                        color: 'text.primary'
                    }}
                >
                    Horarios de Atención
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {sortedDays.map(day => {
                    const dayName = dayNames[day] || day;
                    const times = hoursByDay[day].join(', ');
                    return (
                        <Box 
                            key={day}
                            sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                py: 0.25
                            }}
                        >
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    fontWeight: 500,
                                    color: 'text.primary',
                                    minWidth: '80px'
                                }}
                            >
                                {dayName}:
                            </Typography>
                            <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ 
                                    textAlign: 'right',
                                    flex: 1
                                }}
                            >
                                {times}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

export default StoreHours;
export { formatHours };
